import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { HISTORY_FIELD_LABELS, STATUS_FROM_API } from "@/features/tickets/config";
import { TicketHistoryEntry } from "@/features/tickets/types/index";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";

interface TicketHistoryEntryRowProps {
  entry: TicketHistoryEntry;
}

// The backend stores enum values (e.g. TicketStatus.ToString()) verbatim in old_value/new_value.
// Status needs remapping to its display form ("InProgress" -> "In Progress"); every other field's
// stored value (priority, environment, assignee name, tags, free text) is already display-ready.
function formatHistoryValue(field: string, value: string | null): string {
  if (value === null || value === "") {
    return "-";
  }

  if (field === "status") {
    return STATUS_FROM_API[value] ?? value;
  }

  return value;
}

export function TicketHistoryEntryRow({ entry }: TicketHistoryEntryRowProps) {
  const initials = `${entry.changedBy.firstName[0]}${entry.changedBy.lastName[0]}`.toUpperCase();
  const name = `${entry.changedBy.firstName} ${entry.changedBy.lastName}`.trim();
  const fieldLabel = HISTORY_FIELD_LABELS[entry.field] ?? entry.field;
  const oldValue = formatHistoryValue(entry.field, entry.oldValue);
  const newValue = formatHistoryValue(entry.field, entry.newValue);
  const relativeDate = formatRelativeDate(entry.changedAt);
  const absoluteDate = formatAbsoluteDateTime(entry.changedAt);

  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <Avatar size="sm">
        <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex flex-1 flex-col gap-1 min-w-0">
        <p className="text-xs leading-snug">
          <span className="font-semibold text-foreground">{name}</span>
          <span className="text-muted-foreground"> changed </span>
          <span className="font-semibold text-foreground">{fieldLabel}</span>
          <span className="text-muted-foreground"> from </span>
          <span className="line-through text-muted-foreground">{oldValue}</span>
          <span className="text-muted-foreground"> to </span>
          <span className="font-semibold text-foreground">{newValue}</span>
        </p>
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <span className="w-fit text-[11px] text-muted-foreground">{relativeDate}</span>
          </TooltipTrigger>
          <TooltipContent>{absoluteDate}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
